import { DoNotDisturbAlt } from "@mui/icons-material";
import { Card, CircularProgress, Stack, Typography } from "@mui/material";
import { type Currency } from "@prisma/client";
import classNames from "classnames";
import React from "react";
import { type Input as MarketDataInput } from "~/components/market-data/MarketDataBrowser";
import { TitledTooltip } from "~/components/ui/core/TitledTooltip";
import { DataQualityIndicator } from "~/components/ui/DataQualityIndicator";
import { type LocationForSelector } from "~/components/ui/LocationSelector";
import { formatMeasure } from "~/components/ui/MeasureSelector";
import { Money } from "~/components/ui/Money";
import { useFeatureFlags } from "~/hooks/useFeatureFlags";
import { useSession } from "~/hooks/useSession";
import { useI18n } from "~/lib/i18n/useI18n";
import { orderBy } from "~/lib/lodash";
import { useFetchAllowedCompanyBenchmarkLevelsQuery } from "~/pages/api/benchmark-level/fetch-allowed-company-benchmark-levels";
import { EmployeeLevel, formatBenchmarkLevel, formatLevel } from "~/services/employee/employeeLevel";
import { type MarketDataStats, MarketDataStatsSource } from "~/services/marketCompensationStats";
import { type PercentileBounds } from "~/services/percentiles";

type Props = {
  useTestData: boolean;
  height: number;
  isLoading: boolean;
  disableInteractions?: boolean;
  currency: Currency;
  categories: EmployeeLevel[];
  input: MarketDataInput;
  market: {
    locations?: LocationForSelector[];
    byLevel: {
      [key in EmployeeLevel | number]?: MarketDataStats | null;
    };
  };
  comparison?: {
    locations?: LocationForSelector[];
    byLevel: {
      [key in EmployeeLevel | number]?: MarketDataStats | null;
    };
  };
  percentiles: PercentileBounds;
};

type Row = {
  level: EmployeeLevel | number;
  type: StatsType;
  stats: MarketDataStats | null;
  companyStats?: MarketDataStats | null;
};

type StatsType = "market" | "company" | "comparison";

export const MarketDataTable: React.FC<Props> = (props) => {
  const { t } = useI18n();
  const { user } = useSession();
  const { currency, categories, market, comparison } = props;

  const benchmarkLevelIds = props.input.benchmarkLevelIds;
  const { CAN_ACCESS_LEVEL_FRAMEWORKS } = useFeatureFlags();
  const { data: allowedBenchmarkLevels } = useFetchAllowedCompanyBenchmarkLevelsQuery();

  const canAccessBenchmarkLevels = CAN_ACCESS_LEVEL_FRAMEWORKS || props.input.useBenchmarkLevelsInShare;

  const orderedCategories = canAccessBenchmarkLevels
    ? benchmarkLevelIds.sort((a, b) => a - b)
    : orderBy(categories, (level) => {
        return Object.keys(EmployeeLevel).indexOf(level);
      });

  const stats = orderedCategories.flatMap((level) => {
    const stats: Row[] = [];

    const marketStats = market.byLevel[level];
    if (marketStats !== undefined) {
      stats.push({
        type: "market",
        level: level as EmployeeLevel,
        stats: marketStats,
      });
    }

    if (comparison) {
      const comparisonStats = comparison.byLevel[level];

      if (comparisonStats !== undefined) {
        stats.push({
          type: "comparison",
          level: level as EmployeeLevel,
          stats: comparisonStats,
        });
      }
    }

    return stats;
  });

  const percentiles = {
    low: Math.round(props.percentiles.low * 100),
    middle: Math.round(props.percentiles.middle * 100),
    high: Math.round(props.percentiles.high * 100),
  };

  const measures = ["baseSalary", "onTargetEarnings", "totalCash"] as const;

  return (
    <Stack className={classNames({ "relative rounded-md border-0 bg-slate-500": props.isLoading })}>
      <Card
        variant="outlined"
        className={classNames({ "flex-1 overflow-x-auto p-0": true, "opacity-75": props.isLoading })}
      >
        <CircularProgress
          className={classNames({ "absolute left-1/2 top-1/3 z-99 ": props.isLoading, "hidden": !props.isLoading })}
        />
        <table className="w-full">
          <thead className="bg-gray-50">
            <tr>
              <td colSpan={2} />

              {measures.map((measure) => (
                <td key={measure} className="border-l border-gray-200 py-3 text-center" colSpan={3} valign="bottom">
                  <Typography variant="subtitle2" color="gray.600">
                    {formatMeasure(t, measure)}
                  </Typography>
                </td>
              ))}
            </tr>

            <tr className="border-b text-sm text-gray-700">
              <th className="pl-6 text-left">
                <Typography variant="subtitle2" color="gray.600">
                  {t("pages.market-data.market-data-table.level")}
                </Typography>
              </th>

              <th className="px-3 py-3 text-left font-medium">
                <Typography noWrap variant="subtitle2" color="gray.600">
                  {t("pages.market-data.market-data-table.data-quality")}
                </Typography>
              </th>

              {measures.map((measure) => (
                <React.Fragment key={measure}>
                  <th className="border-l border-gray-200 px-4 py-3 text-right font-medium">
                    <TitledTooltip
                      placement="bottom"
                      heading={`${percentiles.low}th Percentile`}
                      text={`Shows the salary point below which ${percentiles.low}% of employees with this role and
                          seniority are being paid`}
                    >
                      <Typography variant="subtitle2" color="gray.600">
                        P{percentiles.low}
                      </Typography>
                    </TitledTooltip>
                  </th>

                  <th className="px-4 py-3 text-right">
                    <TitledTooltip
                      heading={`${percentiles.middle}th Percentile`}
                      text={`Shows the salary point below which ${percentiles.middle}% of employees with this role and seniority are being paid`}
                      placement="bottom"
                    >
                      <Typography fontWeight="600" color="gray.600">
                        P{percentiles.middle}
                      </Typography>
                    </TitledTooltip>
                  </th>

                  <th className="px-4 py-3 text-right">
                    <TitledTooltip
                      heading={`${percentiles.high}th Percentile`}
                      text={`Shows thes salary point below which ${percentiles.high}% of employees with this role and seniority are being paid`}
                      placement="bottom"
                    >
                      <Typography variant="subtitle2" color="gray.600">
                        P{percentiles.high}
                      </Typography>
                    </TitledTooltip>
                  </th>
                </React.Fragment>
              ))}
            </tr>
          </thead>

          <tbody>
            {stats.map((row, index) => (
              <tr key={index} className={classNames({ "bg-blue-50": row.type === "comparison" })}>
                <th className="pl-6 text-left">
                  {row.type === "market" && (
                    <Typography textTransform="capitalize" variant="subtitle2">
                      {!canAccessBenchmarkLevels
                        ? formatLevel(t, row.level as EmployeeLevel, user?.company.useAdvancedLevels ?? true)
                        : allowedBenchmarkLevels &&
                          `${formatBenchmarkLevel(row.level as number, allowedBenchmarkLevels)}`}
                    </Typography>
                  )}
                </th>

                <td className="px-3 py-4 text-right">
                  {!!row.stats ? (
                    <DataQualityIndicator
                      dataQuality={row.stats.baseSalary.dataQuality}
                      dataQualityDetailledByJob={row.stats.baseSalary.dataQualityDetailledByJob}
                      {...(row.stats.source === MarketDataStatsSource.DATASET && {
                        employeeCount: row.stats.count,
                        companyCount: row.stats.distinctCompaniesCount,
                      })}
                    />
                  ) : (
                    <Stack direction="row" spacing={1}>
                      <DoNotDisturbAlt fontSize="small" className="text-gray-300" />
                      <Typography variant="caption" color="gray.600">
                        {t("pages.market-data.market-data-table.sample-too-small")}
                      </Typography>
                    </Stack>
                  )}
                </td>

                {measures.map((measure) => (
                  <React.Fragment key={measure}>
                    <td className="border-l border-gray-200 px-4 py-3 text-right ">
                      {!!row.stats?.baseSalary.low ? (
                        <Typography variant="caption">
                          <Money amount={row.stats[measure].low.value} currency={currency} roundTo={100} />
                        </Typography>
                      ) : (
                        <EmptyCell />
                      )}
                    </td>

                    <td className="px-4 py-3 text-right font-medium">
                      {!!row.stats?.[measure].middle ? (
                        <Typography variant="caption" fontWeight={600}>
                          <Money amount={row.stats[measure].middle.value} currency={currency} roundTo={100} />
                        </Typography>
                      ) : (
                        <EmptyCell />
                      )}
                    </td>

                    <td className="px-4 py-3 text-right text-sm">
                      {!!row.stats?.baseSalary.high ? (
                        <Typography variant="caption">
                          <Money amount={row.stats[measure].high.value} currency={currency} roundTo={100} />
                        </Typography>
                      ) : (
                        <EmptyCell />
                      )}
                    </td>
                  </React.Fragment>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </Card>
    </Stack>
  );
};

const EmptyCell: React.FC = () => {
  return <span className="mr-px text-xs text-gray-400">-</span>;
};
